// components/common/UIElements.jsx - Shared saffron-theme building blocks
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const Panel = ({ children, className = '' }) => (
    <div
        className={`rounded-2xl bg-white ${className}`}
        style={{ border: '1px solid rgba(255,125,56,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
    >
        {children}
    </div>
);

export const StatusDot = ({ status, onClick, loading }) => {
    const isActive = status === "active";

    return (
        <div className="flex items-center justify-start gap-2 whitespace-nowrap">
            <button
                type="button"
                onClick={onClick}
                disabled={loading}
                title={isActive ? "Deactivate" : "Activate"}
                aria-label={isActive ? "Deactivate" : "Activate"}
                className={`
                    relative inline-flex h-6 w-11 shrink-0
                    items-center rounded-full
                    transition-colors duration-200 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-offset-1
                    disabled:cursor-not-allowed disabled:opacity-50
                    ${isActive
                        ? "bg-orange-500 focus:ring-orange-400"
                        : "bg-gray-400 focus:ring-gray-300"
                    }
                `}
            >
                <span
                    className={`
                        inline-block h-4 w-4 rounded-full bg-white
                        shadow-md
                        transition-transform duration-200 ease-in-out
                        ${isActive
                            ? "translate-x-6"
                            : "translate-x-1"
                        }
                    `}
                />
            </button>

            <span
                className={`
                    text-xs sm:text-sm font-medium
                    ${isActive ? "text-orange-500" : "text-gray-500"}
                `}
            >
                {isActive ? "Active" : "Inactive"}
            </span>
        </div>
    );
};

export const Pagination = ({ current, total, onChange }) => {
    if (total <= 1) return null;
    const pages = [];
    for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) pages.push(i);
        else if (i === current - 2 || i === current + 2) pages.push('...');
    }
    const deduped = pages.filter((p, i) => !(p === '...' && pages[i - 1] === '...'));

    return (
        <div className="flex items-center gap-1">
            <button
                onClick={() => onChange(current - 1)}
                disabled={current === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-orange-400 hover:bg-orange-50"
            >
                <ChevronLeft size={15} />
            </button>
            {deduped.map((p, i) =>
                p === '...' ? (
                    <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">…</span>
                ) : (
                    <button
                        key={p}
                        onClick={() => onChange(p)}
                        className="w-8 h-8 rounded-lg text-xs font-medium transition-all"
                        style={
                            p === current
                                ? { background: 'linear-gradient(135deg,#FF7D38,#FF6B1A)', color: '#fff', boxShadow: '0 0 10px rgba(255,125,56,0.4)' }
                                : { color: '#4a4a4a' }
                        }
                    >
                        {p}
                    </button>
                )
            )}
            <button
                onClick={() => onChange(current + 1)}
                disabled={current === total}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-orange-400 hover:bg-orange-50"
            >
                <ChevronRight size={15} />
            </button>
        </div>
    );
};

export const SummaryCards = ({ items }) => (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        {items.map((s) => (
            <div
                key={s.label}
                className="relative overflow-hidden rounded-2xl p-4 flex items-center gap-3 transition-all hover:scale-105 bg-white border border-orange-200 shadow-sm hover:shadow-md"
            >
                <div className="absolute -top-8 -left-8 w-20 h-20 rounded-full bg-orange-400/10" />
                <div className="absolute -top-3 -left-3 w-28 h-28 rounded-full border border-orange-300/20" />
                <div className="absolute -bottom-5 -right-5 w-12 h-12 rounded-full bg-orange-400/10" />
                <div className="relative w-9 h-9 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-lg">
                    <s.icon size={15} className="text-orange-500" />
                </div>
                <div className="relative">
                    <p className="text-[10px] font-medium text-gray-500">{s.label}</p>
                    <p className="text-xl font-bold text-orange-500">{s.value}</p>
                </div>
            </div>
        ))}
    </div>
);

export const EmptyState = ({ icon: Icon, label }) => (
    <div className="text-center py-16 rounded-2xl bg-white border border-orange-200 text-gray-500">
        <Icon size={32} className="mx-auto mb-3 opacity-30" />
        <p className="text-sm">{label}</p>
    </div>
);

export const timeAgo = (iso) => {
    if (!iso) return '—';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diffMs)) return '—';
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
};

export const initials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('') || '?';

export const AVATAR_GRADIENTS = [
    'from-orange-500 to-amber-600',
    'from-amber-400 to-orange-500',
    'from-orange-600 to-amber-700',
    'from-amber-500 to-yellow-600',
    'from-orange-400 to-amber-500',
    'from-amber-600 to-orange-700',
];

export const gradFor = (seed = '') => {
    const idx = seed.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length;
    return AVATAR_GRADIENTS[idx];
};